import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Building2, Search, ChevronDown, ChevronUp, Calendar, Users, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';
import { BusinessAdminActions, RejectedBusinessesTab } from './AdminBusinessActions';

export function AdminBusiness({ headers }) {
  const [tab, setTab] = useState('pending');
  const [businesses, setBusinesses] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/business-accounts`, { headers });
      setBusinesses(res.data.items || []);
    } catch (e) {
      console.error('Failed to load business accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-businesses`, { headers });
      setPending(res.data.items || []);
    } catch (e) {
      console.error('Failed to load pending businesses');
    }
  };

  useEffect(() => {
    fetchBusinesses();
    fetchPending();
  }, []);

  const approveBusiness = async (userId) => {
    try {
      await axios.post(`${API}/admin/approve-business/${userId}`, {}, { headers });
      toast.success('Business account approved');
      fetchPending();
      fetchBusinesses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const rejectBusiness = async () => {
    if (!rejectDialog) return;
    try {
      await axios.post(`${API}/admin/reject-business/${rejectDialog}`, { reason: rejectReason }, { headers });
      toast.success('Business account rejected');
      setRejectDialog(null);
      setRejectReason('');
      fetchPending();
      fetchBusinesses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
  };

  const toggleExpand = async (bizId) => {
    if (expanded[bizId]) {
      setExpanded(prev => ({ ...prev, [bizId]: null }));
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/business-accounts/${bizId}/details`, { headers });
      setExpanded(prev => ({ ...prev, [bizId]: res.data }));
    } catch {
      setExpanded(prev => ({ ...prev, [bizId]: { sub_accounts: [], transactions: [], invitations: [] } }));
    }
  };

  const filtered = businesses.filter(b => {
    if (b.account_status === 'REJECTED') return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (b.full_name || '').toLowerCase().includes(s)
      || (b.email || '').toLowerCase().includes(s)
      || (b.business_info?.company_name || '').toLowerCase().includes(s)
      || (b.paymig_id || '').includes(s);
  });

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return '-'; }
  };

  const getStatusBadge = (status, id) => {
    if (status === 'ACTIVE') return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>;
    if (status === 'PENDING_APPROVAL') return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>;
    if (status === 'REJECTED') return <Badge className="bg-red-100 text-red-700 text-[10px]">Rejected</Badge>;
    if (status === 'SUSPENDED') return <Badge className="bg-orange-100 text-orange-800 text-[10px]" data-testid={`suspended-badge-${id}`}>Suspended</Badge>;
    return <Badge variant="secondary" className="text-[10px]">{status || 'Unknown'}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="admin-business">
      {/* Tab bar */}
      <div className="flex items-center gap-4 border-b pb-3">
        <Button
          variant={tab === 'pending' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setTab('pending')}
          className="gap-1.5"
          data-testid="admin-biz-tab-pending"
        >
          <Clock className="h-4 w-4" />
          Pending Approvals
          {pending.length > 0 && (
            <Badge variant="destructive" className="ml-1 h-5 min-w-5 text-[10px] px-1.5">{pending.length}</Badge>
          )}
        </Button>
        <Button
          variant={tab === 'all' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setTab('all')}
          className="gap-1.5"
          data-testid="admin-biz-tab-all"
        >
          <Building2 className="h-4 w-4" />
          All Business Accounts ({businesses.filter(b => b.account_status !== 'REJECTED').length})
        </Button>
        <Button
          variant={tab === 'rejected' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setTab('rejected')}
          className="gap-1.5"
          data-testid="admin-biz-tab-rejected"
        >
          <XCircle className="h-4 w-4" />
          Rejected
        </Button>
      </div>
      {tab === 'rejected' && <RejectedBusinessesTab headers={headers} onReapproved={fetchBusinesses} />}

      {/* PENDING APPROVALS TAB */}
      {tab === 'pending' && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Pending Business Approvals
          </h2>
          
          {pending.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                <p>No pending approvals</p>
              </CardContent>
            </Card>
          ) : (
            pending.map(biz => (
              <Card key={biz.id} className="border-amber-200" data-testid={`pending-biz-${biz.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center mt-0.5">
                        <Building2 className="h-6 w-6 text-amber-700" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-semibold text-base">{biz.company_name || biz.full_name}</p>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          <div>Contact: <strong>{biz.full_name}</strong></div>
                          <div>Email: <strong>{biz.email}</strong></div>
                          <div>Phone: <strong>{biz.phone || 'N/A'}</strong></div>
                          <div>Org Number: <strong>{biz.org_number || 'N/A'}</strong></div>
                          <div>Applied: <strong>{formatDate(biz.created_at)}</strong></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => approveBusiness(biz.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                        data-testid={`approve-biz-${biz.id}`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => { setRejectDialog(biz.id); setRejectReason(''); }}
                        className="gap-1.5"
                        data-testid={`reject-biz-${biz.id}`}
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ALL BUSINESS ACCOUNTS TAB */}
      {tab === 'all' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">All Business Accounts ({filtered.length})</h2>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search by name, email, PayMig ID..." value={search} onChange={e => setSearch(e.target.value)} data-testid="admin-biz-search" />
          </div>

          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No business accounts found</div>
          ) : (
            <div className="space-y-3">
              {filtered.map(biz => (
                <Card key={biz.id} data-testid={`biz-card-${biz.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-700" />
                        </div>
                        <div>
                          <p className="font-medium">{biz.business_info?.company_name || biz.full_name}</p>
                          <p className="text-xs text-muted-foreground">{biz.email} · PayMig: {biz.paymig_id || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(biz.account_status, biz.id)}
                        <Badge variant="secondary" className="text-xs">{biz.sub_account_count} sub-accounts</Badge>
                        {biz.business_info?.swish_merchant_number && (
                          <Badge variant="outline" className="text-xs">Swish: {biz.business_info.swish_merchant_number}</Badge>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => toggleExpand(biz.id)} data-testid={`expand-biz-${biz.id}`}>
                          {expanded[biz.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mt-3 text-xs text-muted-foreground">
                      <div>Phone: {biz.phone || 'N/A'}</div>
                      <div>Org#: {biz.business_info?.org_number || 'N/A'}</div>
                      <div>Created: {formatDate(biz.created_at)}</div>
                      <div>
                        Email: {biz.email_verified ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Verified</Badge> : <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Pending</Badge>}
                      </div>
                    </div>
                    <BusinessAdminActions biz={biz} headers={headers} onChanged={fetchBusinesses} />

                    {expanded[biz.id] && (
                      <div className="mt-4 space-y-4 border-t pt-4">
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Sub-Accounts</p>
                          {(expanded[biz.id].sub_accounts || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">No sub-accounts</p>
                          ) : (
                            <div className="space-y-1.5">
                              {expanded[biz.id].sub_accounts.map(sa => (
                                <div key={sa._id || sa.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                  <div>
                                    <span className="font-medium">{sa.name}</span>
                                    <span className="text-muted-foreground ml-2">PayMig: {sa.paymig_id || 'N/A'}</span>
                                    {sa.connected_swish_id && <span className="text-muted-foreground ml-2">Swish: {sa.connected_swish_id}</span>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={sa.type === 'EVENT' ? 'default' : 'secondary'} className="text-[10px]">{sa.type}</Badge>
                                    <Badge variant={sa.status === 'ACTIVE' ? 'default' : 'destructive'} className="text-[10px]">{sa.status}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">Recent Transactions ({expanded[biz.id].transaction_count || 0})</p>
                          {(expanded[biz.id].transactions || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">No transactions</p>
                          ) : (
                            <div className="overflow-x-auto border rounded text-xs">
                              <table className="min-w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="p-2 text-left">From</th>
                                    <th className="p-2 text-left">To</th>
                                    <th className="p-2 text-left">Amount</th>
                                    <th className="p-2 text-left">Sub-Account</th>
                                    <th className="p-2 text-left">Status</th>
                                    <th className="p-2 text-left">Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expanded[biz.id].transactions.map((t, i) => (
                                    <tr key={i} className="border-t">
                                      <td className="p-2">{t.from_name}</td>
                                      <td className="p-2">{t.to_name}</td>
                                      <td className="p-2 font-medium">{t.amount} SEK</td>
                                      <td className="p-2">{t.sub_account_name || '-'}</td>
                                      <td className="p-2"><Badge variant={t.status === 'COMPLETED' ? 'default' : 'secondary'} className="text-[10px]">{t.status}</Badge></td>
                                      <td className="p-2">{formatDate(t.created_at)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Invitations</p>
                          {(expanded[biz.id].invitations || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">No invitations</p>
                          ) : (
                            <div className="space-y-1.5">
                              {expanded[biz.id].invitations.map((inv, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                  <div>
                                    <span className="font-medium">{inv.user_name}</span>
                                    <span className="text-muted-foreground ml-2">{inv.sub_account_name}</span>
                                  </div>
                                  <Badge variant={inv.status === 'ACCEPTED' ? 'default' : inv.status === 'PENDING' ? 'secondary' : 'destructive'} className="text-[10px]">{inv.status}</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => { if (!o) setRejectDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Business Application</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium">Reason (optional)</label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this application is being rejected..."
              className="mt-1"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={rejectBusiness} data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
